import type { ReactNode } from 'react';

import { AlertTriangle, Inbox, SearchX } from 'lucide-react';

import { cx } from '@/lib/cx';

import { Button } from './Button';
import styles from './EmptyState.module.css';

export type EmptyStateProps = {
  title: string;
  body?: ReactNode;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void } | undefined;
  tone?: 'neutral' | 'danger';
  className?: string;
};

export function EmptyState({
  title,
  body,
  icon,
  action,
  tone = 'neutral',
  className,
}: EmptyStateProps) {
  return (
    <div className={cx(styles.empty, tone === 'danger' && styles.danger, className)}>
      <span className={styles.icon} aria-hidden="true">
        {icon ?? <Inbox size={21} />}
      </span>
      <p className={styles.title}>{title}</p>
      {body ? <p className={styles.body}>{body}</p> : null}
      {action ? (
        <Button variant="ghost" className={styles.action} onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Nothing here yet, and nothing is filtered out either.
 *
 * Kept distinct from the filtered case on purpose (plan 9.4). "No candidates yet"
 * and "no candidates match these filters" call for completely different next steps,
 * and showing the wrong one sends people looking for a problem that is not there.
 */
export function NoData({
  title,
  body,
  action,
  icon,
}: Omit<EmptyStateProps, 'tone' | 'className'>) {
  return <EmptyState title={title} body={body} action={action} icon={icon} />;
}

export function NoResults({
  onClear,
  label = 'items',
}: {
  onClear: () => void;
  label?: string;
}) {
  return (
    <EmptyState
      icon={<SearchX size={21} />}
      title={`No ${label} match these filters`}
      body="Adjust the search term or filters to widen the results."
      action={{ label: 'Clear filters', onClick: onClear }}
    />
  );
}

/** A failed load. Never a blank page, and always with a way to try again (plan 9.4). */
export function ErrorState({
  message = 'Something went wrong loading this.',
  onRetry,
}: {
  message?: string | undefined;
  onRetry?: (() => void) | undefined;
}) {
  return (
    <EmptyState
      tone="danger"
      icon={<AlertTriangle size={21} />}
      title="This did not load"
      body={message}
      action={onRetry ? { label: 'Try again', onClick: onRetry } : undefined}
    />
  );
}
